import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Save, ArrowLeft, LayoutDashboard, ChevronRight, Users, MapPin, Building2, GitBranch, Lock, Archive, Briefcase, Info, Shield } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { normalizeMessageVariables, logCampaignActivity } from "@/lib/campaignHelpers";
import { saveCampaignSequences } from "@/lib/sequenceRows";
import { SequenceTimeline } from "@/components/simplified-onboarding/SequenceTimeline";
import { cn } from "@/lib/utils";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAccountCapacity } from "@/hooks/useAccountCapacity";



export default function SimplifiedCampaignSettings() {
    const { id: campaignId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const returnTo = searchParams.get('returnTo'); // For navigation back to origin page
    const queryClient = useQueryClient();
    const { workspace, isSuperAdmin } = useWorkspace();

    // Check if this workspace is managed by admin (lock mode)
    // Super admins always have full access regardless of workspace settings
    // campaigns_managed_by_admin is now an optional feature toggle, not a lock
    const isManagedByAdmin = workspace?.campaigns_managed_by_admin || false;


    const location = useLocation();
    const isSequencePage = location.pathname.endsWith('/sequence');
    const isAudiencePage = location.pathname.endsWith('/audience');
    const isDedicatedPage = isSequencePage || isAudiencePage;

    // Determine initial tab based on route first, then params/defaults
    const getInitialTab = () => {
        if (isSequencePage) return "sequence";
        if (isAudiencePage) return "audience";
        return searchParams.get('tab') || "sequence";
    };

    const initialTab = getInitialTab();
    const [currentTab, setCurrentTabState] = useState(initialTab);

    // Two-way sync currentTab ↔ ?tab= URL param so revisiting the page
    // (back/forward navigation) restores the previously selected tab.
    const setCurrentTab = (tab: string) => {
        setCurrentTabState(tab);
        // Only sync to URL when not on a dedicated /sequence or /audience route
        // (those routes determine the tab from the path itself).
        if (!isDedicatedPage) {
            const next = new URLSearchParams(searchParams);
            next.set('tab', tab);
            navigate({ search: next.toString() }, { replace: true });
        }
    };

    // Keep currentTab in sync if the URL changes externally (e.g., browser back).
    useEffect(() => {
        const urlTab = isSequencePage
            ? "sequence"
            : isAudiencePage
                ? "audience"
                : searchParams.get('tab');
        if (urlTab && urlTab !== currentTab) {
            setCurrentTabState(urlTab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, isSequencePage, isAudiencePage]);
    const [isSaving, setIsSaving] = useState(false);
    const [validationError, setValidationError] = useState("");
    const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
    const [nextPath, setNextPath] = useState<string | null>(null);

    const [scheduleData, setScheduleData] = useState({
        maxConnectionsDaily: 20,
        maxMessagesDaily: 20,
        timezone: "America/New_York",
        workingHoursStart: 9,
        workingHoursEnd: 18,
        workingDays: [1, 2, 3, 4, 5] as number[], // Monday-Friday
        is24x7: false,
        skipAlreadyConnected: false,
    });

    const [originalScheduleData, setOriginalScheduleData] = useState(scheduleData);
    const [sequences, setSequences] = useState<any[]>([]);
    const [originalSequences, setOriginalSequences] = useState<any[]>([]);

    // Store pre-24/7 settings for toggle memory
    const [pre24x7Settings, setPre24x7Settings] = useState<{
        days: number[];
        startHour: number;
        endHour: number;
    } | null>(null);

    // Validate working hours
    const validateWorkingHours = (start: number, end: number): boolean => {
        if (start >= end) {
            setValidationError("Working hours start must be before end time");
            return false;
        }
        setValidationError("");
        return true;
    };

    // Validate sequences
    const validateSequences = (): boolean => {
        if (sequences.length === 0) {
            setValidationError("At least one sequence step is required");
            return false;
        }

        // Check that last step is an "end" action
        const lastStep = sequences[sequences.length - 1];
        if (lastStep.action_type !== "end") {
            setValidationError("Sequence must end with an 'end' action");
            return false;
        }

        // Validate message steps have templates
        for (const seq of sequences) {
            if ((seq.action_type === "message" || seq.action_type === "connection") && seq.include_message) {
                if (!seq.message_template || seq.message_template.trim() === "") {
                    setValidationError(`Sequence step ${seq.step_number} requires a message template`);
                    return false;
                }
            }

            // Validate delay_hours
            if (seq.delay_hours !== null && seq.delay_hours !== undefined && seq.delay_hours < 0) {
                setValidationError(`Sequence step ${seq.step_number} has invalid delay hours`);
                return false;
            }
        }

        setValidationError("");
        return true;
    };

    // Check for unsaved changes
    const hasUnsavedChanges = (): boolean => {
        const scheduleChanged = JSON.stringify(scheduleData) !== JSON.stringify(originalScheduleData);
        const sequencesChanged = JSON.stringify(sequences) !== JSON.stringify(originalSequences);
        return scheduleChanged || sequencesChanged;
    };

    // Helper function to extract available variables from targets - ALWAYS LOWERCASE
    const getAvailableVariablesFromTargets = (targets: any[]): string[] => {
        if (!targets || targets.length === 0) return [];

        const standardVars = ['first_name', 'last_name', 'company', 'title'];
        const systemFields = ['linkedin_url', 'link_type', 'sales_nav_url', 'profile_url', 'provider_id'];

        // Get custom fields from first target's custom_data - ALWAYS LOWERCASE
        const firstTarget = targets[0];
        const customFields = firstTarget?.custom_data
            ? Object.keys(firstTarget.custom_data)
                .filter(key => {
                    const lowerKey = key.toLowerCase();
                    return !standardVars.includes(lowerKey) && !systemFields.includes(lowerKey);
                })
                .map(key => key.toLowerCase()) // Normalize to lowercase
            : [];

        // Check which standard vars exist in targets
        const availableStandardVars = standardVars.filter(varName => {
            return targets.some(t => t[varName]);
        });

        return [...availableStandardVars, ...customFields];
    };

    // Fetch campaign data
    const { data: campaign } = useQuery({
        queryKey: ["campaign-edit", campaignId],
        queryFn: async () => {
            if (!campaignId) return null;
            const { data, error } = await supabase
                .from("campaigns")
                .select(`
          *,
          clients (
            id,
            email,
            status,
            is_premium
          )
        `)
                .eq("id", campaignId)
                .single();

            if (error) throw error;
            return data;
        },
        enabled: !!campaignId,
    });

    // Check if campaign is running - prevent editing critical settings
    const isCampaignRunning = campaign?.status === 'active';
    const isArchived = campaign?.status === 'archived';

    // Fetch targets for variable extraction and preview
    const { data: targets } = useQuery({
        queryKey: ["campaign-targets-edit", campaignId],
        queryFn: async () => {
            if (!campaignId) return [];
            const { data, error } = await supabase
                .from("targets")
                .select("*")
                .eq("campaign_id", campaignId)
                .limit(20); // Fetch up to 20 targets for preview dropdown

            if (error) throw error;
            return data;
        },
        enabled: !!campaignId,
    });

    // Fetch sequences
    const { data: campaignSequences } = useQuery({
        queryKey: ["sequences-edit", campaignId],
        queryFn: async () => {
            if (!campaignId) return [];
            const { data, error } = await supabase
                .from("sequences")
                .select("*")
                .eq("campaign_id", campaignId)
                .order("step_number", { ascending: true });

            if (error) throw error;
            return data;
        },
        enabled: !!campaignId,
    });

    // Fetch managed campaign request if this campaign is linked to one
    const { data: managedRequest } = useQuery({
        queryKey: ["managed-request-for-campaign", campaignId],
        queryFn: async () => {
            if (!campaignId) return null;
            const { data, error } = await supabase
                .from("managed_campaign_requests")
                .select("*")
                .eq("campaign_id", campaignId)
                .maybeSingle();

            if (error) throw error;
            return data;
        },
        enabled: !!campaignId,
    });

    // Shared capacity source of truth (mirrors the DB account ceiling)
    const { capacity, invalidate: invalidateCapacity } = useAccountCapacity(
        campaign?.client_id,
        campaignId
    );

    



    // Update scheduleData when campaign data loads
    useEffect(() => {
        if (campaign) {
            const is24x7 =
                campaign.working_days?.length === 7 &&
                campaign.working_hours_start === 0 &&
                campaign.working_hours_end === 23;

            const newScheduleData = {
                maxConnectionsDaily: campaign.max_connections_daily || 20,
                maxMessagesDaily: campaign.max_messages_daily || 20,
                timezone: campaign.timezone || "America/New_York",
                workingHoursStart: campaign.working_hours_start ?? 9,
                workingHoursEnd: campaign.working_hours_end ?? 18,
                workingDays: campaign.working_days || [1, 2, 3, 4, 5],
                is24x7,
                skipAlreadyConnected: (campaign as any).skip_already_connected ?? false,
            };

            setScheduleData(newScheduleData);
            setOriginalScheduleData(newScheduleData);
        }
    }, [campaign]);

    // Update sequences when campaignSequences data loads
    useEffect(() => {
        if (campaignSequences) {
            setSequences(campaignSequences);
            setOriginalSequences(campaignSequences);
        }
    }, [campaignSequences]);

    const { data: activityLogs } = useQuery({
        queryKey: ["campaign-logs", campaignId],
        queryFn: async () => {
            if (!campaignId) return [];
            const { data, error } = await supabase
                .from("campaign_activity_logs" as any)
                .select("*")
                .eq("campaign_id", campaignId)
                .order("created_at", { ascending: false });

            if (error) throw error;
            return data;
        },
        enabled: !!campaignId
    });

    const handleSave = async () => {
        setIsSaving(true);
        try {
            if (!campaignId) throw new Error("No campaign ID");

            // Validate sequences
            if (!validateSequences()) {
                toast.error("Invalid sequence configuration");
                setIsSaving(false);
                return;
            }

            // Only validate and save schedule settings if NOT in managed mode
            if (!isManagedByAdmin) {
                // Validate working hours
                if (!validateWorkingHours(scheduleData.workingHoursStart, scheduleData.workingHoursEnd)) {
                    toast.error("Invalid working hours configuration");
                    setIsSaving(false);
                    return;
                }

                // Validate limits against available quota
                if (capacity) {
                    const availableConn = capacity.availableConnections;
                    const availableMsg = capacity.availableMessages;


                    if (scheduleData.maxConnectionsDaily > availableConn) {
                        throw new Error(`Cannot allocate ${scheduleData.maxConnectionsDaily} connections. Only ${availableConn} available.`);
                    }
                    if (scheduleData.maxMessagesDaily > availableMsg) {
                        throw new Error(`Cannot allocate ${scheduleData.maxMessagesDaily} messages. Only ${availableMsg} available.`);
                    }
                }

                // Save campaign settings (only when not managed)
                const { error: campaignError } = await supabase
                    .from("campaigns")
                    .update({
                        max_connections_daily: scheduleData.maxConnectionsDaily,
                        max_messages_daily: scheduleData.maxMessagesDaily,
                        timezone: scheduleData.timezone,
                        working_hours_start: scheduleData.workingHoursStart,
                        working_hours_end: scheduleData.workingHoursEnd,
                        working_days: scheduleData.workingDays,
                        skip_already_connected: scheduleData.skipAlreadyConnected,
                    } as any)
                    .eq("id", campaignId);

                if (campaignError) throw campaignError;

                // Log all individual settings changes
                if (originalScheduleData.maxConnectionsDaily !== scheduleData.maxConnectionsDaily) {
                    await logCampaignActivity(campaignId!, 'connections_limit_changed', {
                        from: originalScheduleData.maxConnectionsDaily,
                        to: scheduleData.maxConnectionsDaily
                    });
                }

                if (originalScheduleData.maxMessagesDaily !== scheduleData.maxMessagesDaily) {
                    await logCampaignActivity(campaignId!, 'messages_limit_changed', {
                        from: originalScheduleData.maxMessagesDaily,
                        to: scheduleData.maxMessagesDaily
                    });
                }

                if (originalScheduleData.timezone !== scheduleData.timezone) {
                    await logCampaignActivity(campaignId!, 'timezone_changed', {
                        from: originalScheduleData.timezone,
                        to: scheduleData.timezone
                    });
                }

                if (originalScheduleData.workingHoursStart !== scheduleData.workingHoursStart ||
                    originalScheduleData.workingHoursEnd !== scheduleData.workingHoursEnd) {
                    await logCampaignActivity(campaignId!, 'working_hours_changed', {
                        from: {
                            start: originalScheduleData.workingHoursStart,
                            end: originalScheduleData.workingHoursEnd
                        },
                        to: {
                            start: scheduleData.workingHoursStart,
                            end: scheduleData.workingHoursEnd
                        }
                    });
                }

                if (JSON.stringify(originalScheduleData.workingDays) !== JSON.stringify(scheduleData.workingDays)) {
                    await logCampaignActivity(campaignId!, 'working_days_changed', {
                        from: originalScheduleData.workingDays,
                        to: scheduleData.workingDays
                    });
                }

                // Schedule (days/hours/timezone) is edited from the campaign page while
                // paused; Resume rebuilds the queue from the saved settings, so no live
                // rescheduling happens here.
            }

            // Only update sequences if they actually changed
            const sequencesChanged = JSON.stringify(sequences) !== JSON.stringify(originalSequences);

            if (sequencesChanged) {
                // Atomic replace — a failed insert can never leave the campaign
                // without a sequence (that is how the connection step vanished).
                await saveCampaignSequences(supabase as any, campaignId!, sequences);


                // Reschedule jobs if campaign is active to apply new message templates
                if (campaign?.status === 'active') {
                    toast.info('Updating scheduled messages...', { duration: 3000 });

                    const { error: rescheduleError } = await supabase.functions.invoke(
                        'reschedule-campaign-jobs',
                        {
                            body: { campaignId }
                        }
                    );

                    if (rescheduleError) {
                        console.error('Failed to reschedule jobs:', rescheduleError);
                        toast.warning('Sequences saved but scheduled jobs may have old messages. Pause and resume campaign to fix.');
                    } else {
                        toast.success('Scheduled jobs updated with new messages!');
                    }
                }

                // Log sequence update activity
                await logCampaignActivity(campaignId!, 'sequences_updated', {
                    step_count: sequences.length
                });
            }

            queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
            queryClient.invalidateQueries({ queryKey: ["campaign-edit", campaignId] });
            queryClient.invalidateQueries({ queryKey: ["sequences-edit", campaignId] });
            queryClient.invalidateQueries({ queryKey: ["campaign-logs", campaignId] });

        } catch (error: any) {
            console.error("Error saving campaign:", error);
            toast.error("Failed to save campaign: " + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleNavigation = (path: string) => {
        if (hasUnsavedChanges()) {
            setNextPath(path);
            setShowUnsavedWarning(true);
        } else {
            navigate(path);
        }
    };

    const handleToggle24x7 = (checked: boolean) => {
        if (checked) {
            setPre24x7Settings({
                days: scheduleData.workingDays,
                startHour: scheduleData.workingHoursStart,
                endHour: scheduleData.workingHoursEnd,
            });

            setScheduleData({
                ...scheduleData,
                is24x7: true,
                workingDays: [0, 1, 2, 3, 4, 5, 6],
                workingHoursStart: 0,
                workingHoursEnd: 23,
            });
        } else {
            if (pre24x7Settings) {
                setScheduleData({
                    ...scheduleData,
                    is24x7: false,
                    workingDays: pre24x7Settings.days,
                    workingHoursStart: pre24x7Settings.startHour,
                    workingHoursEnd: pre24x7Settings.endHour,
                });
            } else {
                setScheduleData({
                    ...scheduleData,
                    is24x7: false,
                    workingDays: [1, 2, 3, 4, 5],
                    workingHoursStart: 9,
                    workingHoursEnd: 18,
                });
            }
        }
    };

    // Show loading skeleton while data is loading
    const isLoading = !campaign || !campaignSequences || !targets;

    // Calculate available limits (clamped at zero — never show negatives)
    const availableConnections = capacity ? capacity.availableConnections : 100;
    const availableMessages = capacity ? capacity.availableMessages : 100;

    // Check if capacity is exhausted by other campaigns
    const noConnectionCapacity = availableConnections <= 0;
    const noMessageCapacity = availableMessages <= 0;
    const noCapacityAvailable = noConnectionCapacity && noMessageCapacity;


    if (isLoading) {
        return (
            <div className="max-w-[1200px] mx-auto p-8 space-y-8">
                <div className="space-y-4">
                    <Skeleton className="h-8 w-64" />
                    <Skeleton className="h-4 w-96" />
                </div>
                <Skeleton className="h-[600px] w-full" />
            </div>
        );
    }

    return (
        <div className="max-w-[1200px] mx-auto pb-16 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Header */}
            <div className="flex flex-col gap-6 border-b border-border pb-6 mb-8">
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleNavigation(`/campaigns/${campaignId}`)}
                        className="text-muted-foreground hover:text-foreground -ml-2"
                    >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Back to Campaign
                    </Button>
                    <div className="h-4 w-[1px] bg-border" />
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <LayoutDashboard className="w-3.5 h-3.5" />
                        Campaigns
                    </span>
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-sm font-medium">{campaign.name}</span>
                </div>

                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight text-foreground">
                            {isSequencePage ? 'Message Sequence' :
                                isAudiencePage ? 'Target Audience' :
                                    'Message Sequence'}
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            {isSequencePage ? 'Edit your automated outreach messages.' :
                                isAudiencePage ? 'View your campaign target audience.' :
                                    'Edit your automated outreach messages.'}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => handleNavigation(returnTo || `/campaigns/${campaignId}`)}>
                            {isAudiencePage ? "Back" : "Cancel"}
                        </Button>
                        {!isArchived && !isAudiencePage && (
                            <Button
                                onClick={handleSave}
                                disabled={isSaving || !!validationError || isLoading || isCampaignRunning}
                                className="min-w-[140px]"
                            >
                                <Save className="w-4 h-4 mr-2" />
                                {isSaving ? "Saving..." : "Save Changes"}
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full space-y-8">
                {!isDedicatedPage && <TabsList className="w-full justify-start border-b rounded-none h-auto p-0 bg-transparent gap-6">
                    {managedRequest && (
                        <TabsTrigger
                            value="audience"
                            className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-primary data-[state=active]:text-primary border-b-2 border-transparent rounded-none px-4 py-2"
                        >
                            <div className="flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                <span className="font-medium">Target Audience</span>
                            </div>
                        </TabsTrigger>
                    )}
                    <TabsTrigger
                        value="sequence"
                        className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-primary data-[state=active]:text-primary border-b-2 border-transparent rounded-none px-4 py-2"
                    >
                        <div className="flex items-center gap-2">
                            <GitBranch className="w-4 h-4" />
                            <span className="font-medium">Automation Sequence</span>
                        </div>
                    </TabsTrigger>
                </TabsList>}

                {/* TARGET AUDIENCE TAB - Only for managed campaigns */}
                {managedRequest && (
                    <TabsContent value="audience" className="space-y-6 focus-visible:ring-0">
                        <div className="grid gap-6 max-w-4xl animate-in fade-in duration-300">
                            {/* Header */}




                            <div className="grid gap-6">
                                {/* Geography Section */}
                                <Card className="p-6 space-y-4">
                                    <div className="flex items-center gap-2">
                                        <MapPin className="h-5 w-5 text-muted-foreground" />
                                        <h3 className="font-semibold">Geography</h3>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-sm text-muted-foreground">Countries</Label>
                                            <div className="flex flex-wrap gap-1.5">
                                                {managedRequest.target_countries?.length > 0 ? (
                                                    managedRequest.target_countries.map((country: string) => (
                                                        <Badge key={country} variant="secondary" className="text-xs">{country}</Badge>
                                                    ))
                                                ) : (
                                                    <span className="text-sm text-muted-foreground">Not specified</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-sm text-muted-foreground">Cities</Label>
                                            <div className="flex flex-wrap gap-1.5">
                                                {managedRequest.target_cities?.length > 0 ? (
                                                    managedRequest.target_cities.map((city: string) => (
                                                        <Badge key={city} variant="secondary" className="text-xs">{city}</Badge>
                                                    ))
                                                ) : (
                                                    <span className="text-sm text-muted-foreground">Not specified</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </Card>

                                {/* Companies Section */}
                                <Card className="p-6 space-y-4">
                                    <div className="flex items-center gap-2">
                                        <Building2 className="h-5 w-5 text-muted-foreground" />
                                        <h3 className="font-semibold">Companies</h3>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-sm text-muted-foreground">Industries</Label>
                                            <div className="flex flex-wrap gap-1.5">
                                                {managedRequest.target_industries?.length > 0 ? (
                                                    managedRequest.target_industries.map((ind: string) => (
                                                        <Badge key={ind} variant="secondary" className="text-xs">{ind}</Badge>
                                                    ))
                                                ) : (
                                                    <span className="text-sm text-muted-foreground">Not specified</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-sm text-muted-foreground">Company Sizes</Label>
                                            <div className="flex flex-wrap gap-1.5">
                                                {managedRequest.company_sizes?.length > 0 ? (
                                                    managedRequest.company_sizes.map((size: string) => (
                                                        <Badge key={size} variant="secondary" className="text-xs">{size}</Badge>
                                                    ))
                                                ) : (
                                                    <span className="text-sm text-muted-foreground">Not specified</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-sm text-muted-foreground">Departments</Label>
                                            <div className="flex flex-wrap gap-1.5">
                                                {managedRequest.target_departments?.length > 0 ? (
                                                    managedRequest.target_departments.map((dept: string) => (
                                                        <Badge key={dept} variant="secondary" className="text-xs">{dept}</Badge>
                                                    ))
                                                ) : (
                                                    <span className="text-sm text-muted-foreground">Not specified</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-sm text-muted-foreground">Keywords</Label>
                                            <div className="flex flex-wrap gap-1.5">
                                                {managedRequest.company_keywords?.length > 0 ? (
                                                    managedRequest.company_keywords.map((kw: string) => (
                                                        <Badge key={kw} variant="secondary" className="text-xs">{kw}</Badge>
                                                    ))
                                                ) : (
                                                    <span className="text-sm text-muted-foreground">Not specified</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </Card>

                                {/* People Section */}
                                <Card className="p-6 space-y-4">
                                    <div className="flex items-center gap-2">
                                        <Briefcase className="h-5 w-5 text-muted-foreground" />
                                        <h3 className="font-semibold">People</h3>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-sm text-muted-foreground">Job Titles</Label>
                                            <div className="flex flex-wrap gap-1.5">
                                                {managedRequest.job_titles?.length > 0 ? (
                                                    managedRequest.job_titles.map((title: string) => (
                                                        <Badge key={title} variant="secondary" className="text-xs">{title}</Badge>
                                                    ))
                                                ) : (
                                                    <span className="text-sm text-muted-foreground">Not specified</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-sm text-muted-foreground">Seniority Levels</Label>
                                            <div className="flex flex-wrap gap-1.5">
                                                {managedRequest.seniority_levels?.length > 0 ? (
                                                    managedRequest.seniority_levels.map((level: string) => (
                                                        <Badge key={level} variant="secondary" className="text-xs">{level}</Badge>
                                                    ))
                                                ) : (
                                                    <span className="text-sm text-muted-foreground">Not specified</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </Card>

                                {/* Additional Notes */}
                                {managedRequest.additional_notes && (
                                    <Card className="p-6 space-y-4">
                                        <div className="flex items-center gap-2">
                                            <Info className="h-5 w-5 text-muted-foreground" />
                                            <h3 className="font-semibold">Additional Notes</h3>
                                        </div>
                                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{managedRequest.additional_notes}</p>
                                    </Card>
                                )}
                            </div>
                        </div>
                    </TabsContent>
                )}

                <TabsContent value="sequence" className="mt-6 focus-visible:ring-0">
                    {/* Campaign Running - informational (sequence edits are allowed live) */}
                    {isCampaignRunning && (
                        <Alert className="border-primary/40 bg-primary/5 mb-6">
                            <Info className="h-4 w-4 text-primary" />
                            <AlertDescription className="text-sm">
                                <strong>This campaign is active.</strong> Saved message changes will apply to all future scheduled actions automatically. To add or remove steps, pause the campaign first.
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* Archived Warning */}
                    {isArchived && (
                        <Alert className="border-muted-foreground/50 bg-muted/50 mb-6">
                            <Archive className="h-4 w-4 text-muted-foreground" />
                            <AlertDescription className="text-sm">
                                <strong>Campaign is archived.</strong> Settings are read-only.
                            </AlertDescription>
                        </Alert>
                    )}

                    <div className={cn("space-y-6 animate-in fade-in duration-300", isArchived && "opacity-60 pointer-events-none")}>

                        {/* Skip Already Connected Toggle */}
                        <Card className="p-6 space-y-4 border transition-all hover:border-primary/30 hover:shadow-sm">
                            <div className="flex items-center justify-between">
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <Shield className="w-4 h-4 text-muted-foreground" />
                                        <Label className="text-base font-semibold">Skip Already Connected Targets</Label>
                                    </div>
                                    <p className="text-sm text-muted-foreground max-w-lg">
                                        Targets already in your LinkedIn network will be marked as completed — no follow-up messages sent.
                                    </p>
                                </div>
                                <Switch
                                    checked={scheduleData.skipAlreadyConnected}
                                    onCheckedChange={(checked) => setScheduleData({ ...scheduleData, skipAlreadyConnected: checked })}
                                    disabled={isManagedByAdmin || isCampaignRunning || isArchived}
                                />
                            </div>
                        </Card>

                        <SequenceTimeline
                            sequences={sequences}
                            onChange={setSequences}
                            availableVariables={getAvailableVariablesFromTargets(targets || [])}
                            targets={targets || []}
                            campaignStatus={campaign?.status}
                            isPremium={!!(campaign?.clients as any)?.is_premium}
                        />

                    </div>
                </TabsContent>

            </Tabs>

            <AlertDialog open={showUnsavedWarning} onOpenChange={setShowUnsavedWarning}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
                        <AlertDialogDescription>
                            You have unsaved changes. Are you sure you want to leave without saving?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setShowUnsavedWarning(false)}>
                            Continue Editing
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={() => {
                            setShowUnsavedWarning(false);
                            if (nextPath) navigate(nextPath);
                        }}>
                            Discard Changes
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div >
    );
}
